import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, Field, Input } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { fetchCurrentUser, login } from '@/lib/auth';
import { isNative } from '@/lib/native-auth';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(1, 'Senha obrigatória'),
});
type LoginInput = z.infer<typeof loginSchema>;

export const Route = createFileRoute('/login')({
  beforeLoad: async () => {
    try {
      await fetchCurrentUser();
      throw redirect({ to: '/dashboard' });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // Nativo: renderiza o LoginPage do SPA embarcado (email+senha).
        if (isNative()) return;
        // Web: força a tela de login do server.js (Google + email/senha).
        if (typeof window !== 'undefined') {
          window.location.replace('/auth/login');
          throw new Error('redirecting-to-auth');
        }
        return;
      }
      // Nativo offline (erro de rede): mostra o form mesmo assim.
      if (isNative() && e instanceof ApiError) return;
      throw e;
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      await navigate({ to: '/dashboard' });
    },
  });

  return (
    <div className="min-h-screen w-full grid place-items-center bg-bg p-4">
      <Card className="w-full max-w-sm" padding="lg">
        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-semibold tracking-tight text-text">ARA</h1>
            <p className="text-xs text-text-2">Entre com suas credenciais</p>
          </div>

          <Stack gap="md">
            <Field label="E-mail" htmlFor="email" error={form.formState.errors.email?.message}>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                invalid={!!form.formState.errors.email}
                {...form.register('email')}
              />
            </Field>
            <Field label="Senha" htmlFor="password" error={form.formState.errors.password?.message}>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                invalid={!!form.formState.errors.password}
                {...form.register('password')}
              />
            </Field>
          </Stack>

          {mutation.error && (
            <p className="text-xs text-danger">
              {mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao entrar'}
            </p>
          )}

          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Entrando…' : 'Entrar'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
